import { useEffect, useRef, useState, useCallback } from 'react';
import Select2, { components } from 'react-select';
import { selectBaseStyles } from '@components';
import { Api } from '../../../services';

const MenuList = (props) => {
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        props.selectProps?.onLoadMore?.();
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [props.selectProps]);

  return (
    <components.MenuList {...props} innerRef={elRef}>
      {props.children}
    </components.MenuList>
  );
};

const unwrap = (res) => res?.data ?? res ?? {};
const toRelative = (next) => (next ? next.replace(/^\/?api\/?/i, '').replace(/^\//, '') : null);

const buildOptions = (places) =>
  (places ?? [])
    .map((p) => ({ value: p.id, label: p.name }))
    .filter((o) => o.value != null && o.label);

const buildFirstPageUrl = (mapId: string | number, search: string) => {
  const params = new URLSearchParams();
  params.set('pageSize', '50');
  params.set('page', '1');
  params.set('search', search ?? '');
  return `maps/${mapId}/places?${params.toString()}`;
};

const PlaceSelectSearchInput = ({
  mapId,
  selectedOption,
  onPlaceChange,
  placeholder = 'Search for a Place...',
  disabled,
}) => {
  const [options, setOptions] = useState<any[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [localValue, setLocalValue] = useState<any>(selectedOption ?? null);

  const loadingRef = useRef(false);

  useEffect(() => {
    setLocalValue(selectedOption ?? null);
  }, [selectedOption]);

  useEffect(() => {
    if (disabled || !mapId) return;
    // fetch places...
  }, [mapId, disabled]);

  const fetchPage = useCallback(async (url: string, { append }: { append: boolean }) => {
    if (!url) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setIsLoading(true);
    try {
      const res = await Api.get(url);
      const payload = unwrap(res);

      const pageItems = payload.value ?? payload.items ?? [];
      const newOptions = buildOptions(pageItems);

      setOptions((prev) => {
        const merged = append ? [...prev, ...newOptions] : newOptions;
        const seen = new Set();
        return merged.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
      });

      setNextUrl(payload.next ? toRelative(payload.next) : null);
    } catch (e) {
      console.error('Error fetching places:', e);
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // reset + preload whenever map changes
  useEffect(() => {
    if (!mapId) return;

    setOptions([]);
    setNextUrl(null);
    setInputValue('');

    fetchPage(buildFirstPageUrl(mapId, ''), { append: false });
  }, [mapId, fetchPage]);

  const onLoadMore = () => {
    if (nextUrl) fetchPage(nextUrl, { append: true });
  };

  const handleChange = (opt: any, meta: any) => {
    if (meta?.action === 'clear') {
      setLocalValue(null);
      setInputValue('');
      setOptions([]);
      setNextUrl(null);
      onPlaceChange?.(null, null);
      return;
    }

    setLocalValue(opt);
    setInputValue('');
    onPlaceChange?.(opt?.value ?? null, opt?.label ?? null);
  };

  const handleInputChange = (val: string, meta: any) => {
    // Always let react-select update its internal input when it wants to.
    // We only trigger a fetch when the user is actively typing.
    const action = meta?.action;

    if (action !== 'input-change') {
      return val; // ✅ do nothing on set-value / blur / menu-close etc
    }

    if (!mapId) {
      setInputValue(val);
      return val;
    }

    setInputValue(val);
    setNextUrl(null);
    fetchPage(buildFirstPageUrl(mapId, val), { append: false });
    return val;
  };

  return (
    <Select2
      className="w-100"
      options={options}
      value={localValue}
      placeholder={placeholder}
      onChange={handleChange}
      isLoading={isLoading}
      onLoadMore={onLoadMore}
      components={{ MenuList }}
      maxMenuHeight={260}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isDisabled={disabled}
      styles={selectBaseStyles}
    />
  );
};

export default PlaceSelectSearchInput;
